// ── ReassignModal ─────────────────────────────────────────────────────────────
// CISO-only modal to change the assignee and/or verifier on an existing assignment.

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useReassignAssignment } from './api';
import type { Assignment } from '@grc/shared';

interface Props {
  assignment: Assignment;
  controlId: string;
  open: boolean;
  onClose: () => void;
}

export function ReassignModal({ assignment, controlId, open, onClose }: Props) {
  const reassignMutation = useReassignAssignment(controlId);

  const [assigneeEmail, setAssigneeEmail] = useState(assignment.assignee_email ?? '');
  const [assigneeDisplayName, setAssigneeDisplayName] = useState(assignment.assignee_display_name ?? '');
  const [note, setNote] = useState('');

  function handleClose() {
    setAssigneeEmail(assignment.assignee_email ?? '');
    setAssigneeDisplayName(assignment.assignee_display_name ?? '');
    setNote('');
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    reassignMutation.mutate(
      {
        id: assignment.id,
        assignee_email: assigneeEmail.trim() || undefined,
        assignee_display_name: assigneeDisplayName.trim() || undefined,
        note: note.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Assignment updated');
          handleClose();
        },
        onError: (err: unknown) => {
          const msg = (err as { response?: { data?: { errors?: { message?: string }[] } } })
            ?.response?.data?.errors?.[0]?.message ?? 'Failed to reassign';
          toast.error(msg);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reassign Control</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">New assignee email</Label>
            <Input
              type="email"
              placeholder="assignee@company.com"
              value={assigneeEmail}
              onChange={(e) => setAssigneeEmail(e.target.value)}
              className="text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Assignee display name (optional)</Label>
            <Input
              placeholder="Full name"
              value={assigneeDisplayName}
              onChange={(e) => setAssigneeDisplayName(e.target.value)}
              className="text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Reason for reassignment (optional)</Label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Why is this being reassigned?"
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none resize-none"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={handleClose}>Cancel</Button>
            <Button type="submit" disabled={reassignMutation.isPending}>
              {reassignMutation.isPending ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
